/**
 * Shared date/time formatting for VelvetGate.
 * Standard format: "Friday 04-10-2026 08:00 PM"
 */

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function formatTime12(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
}

function formatDatePart(dateStr) {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const [year, month, day] = parts;
  const d = new Date(`${dateStr}T12:00:00`);
  const dayName = !isNaN(d.getTime()) ? DAYS[d.getDay()] : '';
  return `${dayName} ${month}-${day}-${year}`.trim();
}

/**
 * Format an event date + time into the standard display format.
 * @param {string} dateStr - "YYYY-MM-DD"
 * @param {string} timeStr - "HH:mm" (24h)
 * @returns {string} e.g. "Friday 04-10-2026 08:00 PM"
 */
export function formatEventDateTime(dateStr, timeStr) {
  if (!dateStr) return '';
  const datePart = formatDatePart(dateStr);
  const timePart = formatTime12(timeStr);
  return timePart ? `${datePart} ${timePart}` : datePart;
}

/**
 * Format a full event time range with end time and multi-day awareness.
 * @param {object} event - event object with date, time, end_time, end_date, is_multi_day
 * @returns {string} e.g. "Saturday 08-15-2026 8:00 PM – 2:00 AM" or multi-day range
 */
export function formatEventTimeRange(event) {
  if (!event) return '';
  const { date, time, end_time, end_date, is_multi_day } = event;
  if (!date) return '';

  const startDate = formatDatePart(date);
  const startTime = formatTime12(time);

  if (is_multi_day && end_date && end_date !== date) {
    const endDate = formatDatePart(end_date);
    const endTime = formatTime12(end_time);
    if (endTime) {
      return `${startDate} ${startTime} – ${endDate} ${endTime}`;
    }
    return `${startDate} ${startTime} – ${endDate}`;
  }

  if (end_time) {
    return `${startDate} ${startTime} – ${formatTime12(end_time)}`;
  }

  return startTime ? `${startDate} ${startTime}` : startDate;
}

/**
 * Short format for compact displays: "Fri 04-10-2026 8:00 PM"
 */
export function formatEventDateTimeShort(dateStr, timeStr) {
  const full = formatEventDateTime(dateStr, timeStr);
  if (!full) return '';
  const dayEnd = full.indexOf(' ');
  if (dayEnd > 3) return full.slice(0, 3) + full.slice(dayEnd);
  return full;
}
